/* eslint-disable no-lonely-if */
/* eslint-disable react/no-unstable-nested-components */
import './style.scss';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Tooltip } from 'primereact/tooltip';

import { getAllCouncilTypes } from 'apis/councils.api';
import { getAllByUser as getAllOrganizationsByUser } from 'apis/organizations.api';
import { getAll as getAllPrograms } from 'apis/programs.api';
import {
  // approveById,
  changeDeadline,
  deleteById,
  downloadZip,
  exportExcel,
  getAll as getAllProjectProposals,
  getAllResearchTypes,
  restoreById,
  sendRemindEmail,
} from 'apis/projectProposals.api';
import { getAllResearchFieldsByUserId } from 'apis/researchFields.api';

import { Confirm, ViewEmailLogs, ViewHistory } from 'features/ReviseProjectProposal/components';
import SearchFilters from 'features/ReviseProjectProposal/components/SearchFilters';
import SendMail from 'features/ReviseProjectProposal/components/SendMail';

import { AbilityContext, Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import Pagination from 'components/Pagination';
import {
  COUNCIL_STATUS,
  PROJECT_PROPOSAL_NEW_STATUSES,
  // COUNCIL_TYPE_CODE,
  TEMPLATE,
  TIMEZONE_VN,
  TOAST,
} from 'constant';
import ChangeDeadline from 'features/ReviseProjectProposal/components/ChangeDeadline';
import CreateContractForm from 'features/ReviseProjectProposal/components/CreateContractForm';
import RequestUpdateForm from 'features/ReviseProjectProposal/components/RequestUpdateForm';
// import { defaultProjecProposalsFilters } from 'features/ReviseProjectProposal/defaultValues';
import { toast } from 'layout';
import {
  checkIsAllowedCRUDFromContract,
  checkOverdueDatetime,
  getEndingTimeOfDate,
  getMaxDate,
  getNewStatus,
  getProjectTypes,
  getRoles,
  handleEnterKeyPress,
  mapError,
} from 'utils/func';
import useSelectionChange from 'utils/hooks/useSelectionChange';
import { totalExpenditureSchema } from 'features/ReviseProjectProposal/validation';
import ProgramDialog from 'features/ReviseProjectProposal/components/ProgramDialog';
import OpenRevisesForm from 'features/ReviseProjectProposal/components/OpenRevisesForm';
import { useGetProjectLogs } from 'utils/hooks/projectProposals';
import { statusColors } from 'utils/func/dashboard';

const councilTypeToReviseLogTypeMap = {
  preliminarySelectionCouncil: 'REQUEST_EXPLANATION_PREQUALIFICATION',
  specializationCouncil: 'REQUEST_EXPLANATION_SPECIALIZATION',
  financeCouncil: 'REQUEST_EXPLANATION_FINANCE',
};

const councilStatusSeverityMap = {
  notReviewed: 'warning',
  notQualified: 'danger',
  qualified: 'success',
};

function ReviseProjectProposalViewList() {
  // #region data
  const { t } = useTranslation();
  const roles = getRoles();

  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const dialogSendMailRef = useRef();
  const dialogConfirmRef = useRef();
  const dialogRequestUpdateRef = useRef();
  const dialogOpenRevisesRef = useRef();
  const dialogChangeDeadlineRef = useRef();
  const dialogCreateContractRef = useRef();
  const approvalMenu = useRef(null);
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [viewHistoryVisible, setViewHistoryVisible] = useState(false);
  const [projectLogsId, setProjectLogsId] = useState(null);
  const [viewEmailLogsVisible, setViewEmailLogsVisible] = useState(false);
  const [projectProposalId, setProjectProposalId] = useState('');
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [showMode, setShowMode] = useState('hideDeletedProjects');
  const ability = useContext(AbilityContext);
  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);
  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _projectType: { value: null },
    _ownerName: { value: null },
    _organization: { value: null },
    _researchField: { value: null },
    _researchType: { value: null },
    _totalExpenditure: { value: null },
    _yearProposed: { value: null },
    _program: { value: null },
    _submittedAt: { value: null },
    _councilType: { value: null },
    _status: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    organizationId: null,
    yearProposed: null,
    researchFieldId: null,
    researchTypeId: null,
    totalExpenditure: { min: null, max: null },
    programId: null,
    submittedAt: { start: null, end: null },
    councilTypeId: null,
    statusName: null,
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    organizationId: null,
    yearProposed: null,
    researchFieldId: null,
    researchTypeId: null,
    totalExpenditure: { min: null, max: null },
    programId: null,
    submittedAt: { start: null, end: null },
    councilTypeId: null,
    statusName: null,
  });

  // list of project types
  const councilTab = {
    preliminarySelectionCouncil: 1,
    specializationCouncil: 2,
    financeCouncil: 3,
  };

  const projectTypes = getProjectTypes(t);

  const statusesData = Object.values(PROJECT_PROPOSAL_NEW_STATUSES).map((status) => ({
    code: status,
  }));

  const statuses = useMemo(
    () =>
      statusesData.map((e) => ({
        code: e?.code,
        name: e?.code ? t(`newStatuses.${e.code}`) : '',
      })) ?? [],
    [t]
  );

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['projectProposals', pagination, appliedFilters, showMode],
    ({ queryKey: [, _pagination, _filters, _showMode] }) => {
      // Validate if statusName is empty and add default values
      const validatedFilters = {
        ..._filters,
        statusName:
          _filters.statusName && _filters.statusName.length > 0
            ? _filters.statusName
            : statuses.map((status) => status.code),
      };

      return getAllProjectProposals(_pagination, validatedFilters, _showMode);
    },
    {
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const { data: projectHistory } = useGetProjectLogs(projectLogsId, { enabled: !!projectLogsId });

  const { data: organizationsData } = useQuery(
    ['projectProposalOrganizations'],
    getAllOrganizationsByUser
  );

  const organizations = useMemo(
    () =>
      organizationsData?.data?.map((e) => ({
        id: e?.id,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const yearProposedOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= 2019; i -= 1) {
      years.push({ value: i });
    }
    return years;
  }, []);

  const { data: researchFieldsData } = useQuery(
    ['projectProposalResearchFieldsByUserId'],
    getAllResearchFieldsByUserId
  );
  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchFields.${e.code}`) : e.name,
      })) ?? [],
    [researchFieldsData, t]
  );

  const { data: researchTypesData } = useQuery(
    ['projectProposalResearchTypes'],
    getAllResearchTypes
  );
  const researchTypes = useMemo(
    () =>
      researchTypesData?.data.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchTypes.${e.code}`) : e.name,
      })) ?? [],
    [researchTypesData, t]
  );

  const { data: programData } = useQuery(['program'], getAllPrograms);
  const programs = useMemo(() => programData?.data ?? [], [programData]);

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes);
  const councilTypes = useMemo(() => {
    if (councilTypesData?.data?.length > 0) {
      return councilTypesData?.data?.map((item) => ({
        id: item?.id,
        name: item?.code ? t(`council.viewList.${item.code}`) : item?.name,
      }));
    }
    return [];
  }, [councilTypesData, t]);

  const [isVisibleProgramDialog, setIsVisibleProgramDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const handleChangeProgram = (program) => {
    setSelectedProgram(program);
  };

  const {
    mutate: mutateExportExcel,
    isLoading: isExportExcelLoading,
    isError: isExportExcelError,
    error: exportExcelError,
  } = useMutation(exportExcel);

  const {
    mutate: mutateDownloadZip,
    isLoading: isDownloadZipLoading,
    isError: isDownloadZipError,
    error: downloadZipError,
  } = useMutation(downloadZip);

  const {
    mutate: mutateSendRemindEmail,
    isLoading: isSendRemindEmailLoading,
    isError: isSendRemindEmailError,
    error: sendRemindEmailError,
  } = useMutation(sendRemindEmail);

  // const { mutate: mutateApprove } = useMutation(approveById);
  const { mutate: mutateDelete } = useMutation(deleteById);
  const { mutate: mutateRestore } = useMutation(restoreById);
  const { mutate: mutateChangeDeadline, isLoading: isChangeDeadlineLoading } =
    useMutation(changeDeadline);

  const {
    selectedItems,
    // eslint-disable-next-line no-unused-vars
    unselectedItems,
    isSelectedAll,
    isUnselectedSome,
    handleAllRowSelect,
    handleAllRowUnselect,
    handleRowSelect,
    handleRowUnselect,
  } = useSelectionChange(data, pagination);

  const approvalItems = [
    {
      label: t('projectProposal.viewList.approve'),
      icon: 'pi pi-check',
      command: () => {},
    },
    { separator: true },
    {
      label: t('projectProposal.viewList.changeRequest'),
      icon: 'pi pi-refresh',
      command: () => {},
    },
  ];

  // #endregion Data

  // #region Event
  useEffect(() => {
    setShowMode(showMode);
  }, [t]);

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    const modifySubmittedAt = {};
    if (localFilters?.submittedAt) {
      const { start, end } = localFilters.submittedAt;
      if (start || end) {
        modifySubmittedAt.start = start ? new Date(start) : '';
        modifySubmittedAt.end = end ? new Date(end) : '';
      }
    }

    const statusFilters = localFilters?.statusName ?? [
      PROJECT_PROPOSAL_NEW_STATUSES.NEW,
      PROJECT_PROPOSAL_NEW_STATUSES.SUBMIT,
      PROJECT_PROPOSAL_NEW_STATUSES.PREQUALIFICATION,
      PROJECT_PROPOSAL_NEW_STATUSES.SPECIALIZATION,
      PROJECT_PROPOSAL_NEW_STATUSES.FINANCE,
      PROJECT_PROPOSAL_NEW_STATUSES.APPROVED,
      PROJECT_PROPOSAL_NEW_STATUSES.NOT_QUALIFIED,
    ];

    setFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      statusName: statusFilters,
    });
    setAppliedFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      statusName: statusFilters,
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
    setIsLoadedFilterFromLocalStorage(true);
  }, []);

  const handleExportExcel = () => {
    // Call api export excel
    // param: selectedItems,unselectedItems,isSelectedAll,
    // isUnselectedSome,researchFieldId,statusName
    mutateExportExcel(
      {
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = t('projectProposal.viewList.excelName');

            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  const handleDownloadZip = () => {
    // Call api download files
    // param:selectedItems,unselectedItems,isSelectedAll,isUnselectedSome,researchFieldId,statusName
    mutateDownloadZip(
      {
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const contentDisposition = res?.headers['content-disposition'];
            const filenameMatch = contentDisposition?.match(/filename='(.+)'/);
            const filename = filenameMatch ? filenameMatch[1] : 'Danh sach de tai DHQG.zip';
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;

            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  const handleSendMail = (subject, content, _reset, _setVisible) => {
    mutateSendRemindEmail(
      {
        subject,
        content,
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('projectProposal.sendRemindEmailSuccessMessage'));
          handleAllRowUnselect();
          _reset();
          _setVisible(false);
          refetch();
          // reset to empty so that when click view emailLogs again, it refetch emailLogs data
          setProjectProposalId('');
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  // #endregion Event

  // #region Render
  const isRowSelectable = (event) => event.data.deletedAt === null;

  const rowClassName = (value) => (value?.deletedAt !== null ? 'p-disabled' : '');

  const programTemplate = (option) => (
    <div
      className="text-overflow-ellipsis w-26rem overflow-hidden program-filter-list"
      data-pr-tooltip={option.name}
      data-pr-position="right"
      data-pr-at="right+5 top"
      data-pr-my="left center-2"
    >
      {option.name}
      <Tooltip target=".program-filter-list" />
    </div>
  );

  const formatName = (value) => {
    if (value?.deletedAt === null) {
      return (
        <Link
          to={`${value?.reference}?tab=0`}
          className="hover:underline"
          state={{ template: value?.template }}
        >
          {value?.vietnameseName}
        </Link>
      );
    }

    return <span>{value?.vietnameseName}</span>;
  };
  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.name')}
      className="p-column-filter"
      onKeyDown={(e) => {
        handleEnterKeyPress(e, () => {
          setAppliedFilters(filters);
        });
      }}
    />
  );

  const projectTypeFilter = () => (
    <MultiSelect
      value={filters.projectType}
      onChange={(e) => {
        const newFilters = { ...filters, projectType: e.value };
        setFilters(newFilters);
      }}
      options={projectTypes}
      optionLabel="name"
      optionValue="value"
      filter
      showClear
      placeholder={t('projectProposal.viewList.projectType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '16rem', width: '12.5rem' }}
    />
  );
  const formatProjectType = (value) => t(`projectTypes.${value.projectType}`);

  const formatOwner = (value) => {
    const owner = value?.projectProposalContacts.find(
      (contact) => contact?.projectRoleId === 'e609455d-e481-492e-8fca-f631ebf0d464'
    );
    if (owner) return <div>{owner.fullname}</div>;
    return null;
  };
  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.owner')}
      className="p-column-filter"
    />
  );
  const formatOrganization = (value) =>
    t('lang') === 'vi'
      ? value?.projectProposalOrganizations[0]?.name
      : value?.projectProposalOrganizations[0]?.englishName;
  const organizationFilter = () => (
    <MultiSelect
      value={filters.organizationId}
      onChange={(e) => {
        const newFilters = { ...filters, organizationId: e.value };
        setFilters(newFilters);
      }}
      options={organizations}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.organization')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );
  const yearProposedFilter = () => (
    <MultiSelect
      value={filters.yearProposed}
      onChange={(e) => {
        const newFilters = { ...filters, yearProposed: e.value };
        setFilters(newFilters);
      }}
      options={yearProposedOptions}
      optionLabel="value"
      optionValue="value"
      filter
      showClear
      placeholder={t('projectProposal.general.researchTime.yearProposed')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN }) : '';

  const formatSubmittedAt = (value) => formatDate(value?.submittedAt);

  const formatResearchFields = (value) => (
    <div>
      {value?.projectProposalResearchFields
        ?.sort((a, b) => {
          if (a.priority === null) return 1;
          if (b.priority === null) return -1;
          if (a.priority === b) return 0;
          return a.priority < b.priority ? -1 : 1;
        })
        ?.map((researchField, index) => (
          <p key={researchField.researchField.id} className="text-left m-0">
            {`(${researchField?.priority ?? index + 1}) ${t(
              `researchFields.${researchField.researchField.code}`
            )}`}
          </p>
        ))}
    </div>
  );
  const researchFieldFilter = () => (
    <MultiSelect
      value={filters.researchFieldId}
      onChange={(e) => {
        const newFilters = { ...filters, researchFieldId: e.value };
        setFilters(newFilters);
      }}
      options={researchFields}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.researchField')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatResearchType = (value) =>
    value?.researchType?.id ? t(`researchTypes.${value?.researchType.code}`) : '';
  const researchTypeFilter = () => (
    <MultiSelect
      value={filters.researchTypeId}
      onChange={(e) => {
        const newFilters = { ...filters, researchTypeId: e.value };
        setFilters(newFilters);
      }}
      options={researchTypes}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.projectType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '16rem', width: '12.5rem' }}
    />
  );

  const formatTotalExpenditure = (value) => {
    if (value?.totalExpenditure === 0 && value?.proposedExpenditureSummaries?.length > 0) {
      const total = value?.proposedExpenditureSummaries.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0);
      return ((total || 0) / 1000000).toLocaleString('vi');
    }
    return ((value?.totalExpenditure || 0) / 1000000)?.toLocaleString('vi');
  };
  const totalExpenditureFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-3">{t('projectProposal.viewList.from')}</span>
        <InputNumber
          value={filters?.totalExpenditure?.min ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              totalExpenditure: { ...filters.totalExpenditure, min: e.value },
            };
            setFilters(newFilters);
          }}
          className={classNames('currency', {
            'p-invalid': !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            }),
          })}
          tooltip={
            !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            })
              ? t('projectProposal.viewList.validation.min')
              : ''
          }
          tooltipOptions={{ position: 'bottom' }}
          mode="currency"
          currency="VND"
          locale="vi-VN"
          useGrouping
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-3">{t('projectProposal.viewList.to')}</span>
        <InputNumber
          value={filters?.totalExpenditure?.max ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              totalExpenditure: { ...filters.totalExpenditure, max: e.value },
            };
            setFilters(newFilters);
          }}
          className={classNames('currency', {
            'p-invalid': !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            }),
          })}
          tooltip={
            !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            })
              ? t('projectProposal.viewList.validation.max')
              : ''
          }
          tooltipOptions={{ position: 'bottom' }}
          mode="currency"
          currency="VND"
          locale="vi-VN"
          useGrouping
        />
      </div>
    </div>
  );

  const programFilter = () => (
    <MultiSelect
      value={filters.programId}
      onChange={(e) => {
        const newFilters = { ...filters, programId: e.value };
        setFilters(newFilters);
      }}
      options={programs}
      optionLabel="name"
      optionValue="id"
      optionGroupChildren="children"
      filter
      showClear
      placeholder={t('projectProposal.viewList.program')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
      panelStyle={{ maxWidth: '30rem', width: '30rem' }}
      emptyMessage={t('program.viewList.emptyMessage')}
      emptyFilterMessage={t('program.viewList.emptyMessageFilter')}
      title={t('program.viewList.title')}
      itemTemplate={programTemplate}
    />
  );

  const submittedAtFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-2">{t('projectProposal.viewList.from')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.submittedAt?.start ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              submittedAt: { ...filters.submittedAt, start: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-2">{t('projectProposal.viewList.to')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.submittedAt?.end ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              submittedAt: { ...filters.submittedAt, end: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
    </div>
  );

  const formatCouncil = (value) => {
    const projectProposalCouncils = value?.projectProposalCouncils ?? []; // sorted ASC by councilType.order

    if (projectProposalCouncils?.length > 0) {
      return projectProposalCouncils?.map((ppCouncil, index) => {
        const correspondingReviseLog = (value?.projectProposalReviseLogs ?? [])?.find(
          (item) => item?.type === councilTypeToReviseLogTypeMap?.[ppCouncil?.councilType?.code]
        );
        const hasRequest = !!ppCouncil?.councilStatus && !!correspondingReviseLog;

        // 7. có yc giải trình, chủ nhiệm đã giải trình và CHỐT
        // TODO

        let requestStatusIcon = {};
        // có yc giải trình
        if (hasRequest) {
          // 4. có yc giải trình, chủ nhiệm chưa giải trình
          if (correspondingReviseLog?.isOpened && !correspondingReviseLog?.ownerComment) {
            requestStatusIcon = {
              tooltip: t('projectProposal.requestExplanation.statuses.ownerNotAddExplanationYet'),
              className: 'text-red-600',
              icon: 'pi pi-times-circle',
            };
          }
          // 5. có yc giải trình, chủ nhiệm đã giải trình
          if (!correspondingReviseLog?.isOpened && correspondingReviseLog?.ownerComment) {
            requestStatusIcon = {
              tooltip: t('projectProposal.requestExplanation.statuses.ownerHasAddedExplanation'),
              className: 'text-green-600',
              icon: 'pi pi-check-circle',
            };
          }
          // 6. có yc giải trình, chủ nhiệm đã giải trình, cv yêu cầu gtrinh lại
          if (correspondingReviseLog?.isOpened && correspondingReviseLog?.ownerComment) {
            requestStatusIcon = {
              tooltip: t(
                'projectProposal.requestExplanation.statuses.ownerNotUpdateExplanationYet'
              ),
              className: 'text-orange-500',
              icon: 'pi pi-replay',
            };
          }
        }
        if (
          value?.template === TEMPLATE[1595] &&
          ppCouncil?.councilType?.code === 'financeCouncil'
        ) {
          return null;
        }
        return (
          <div key={ppCouncil?.id ?? index} className="flex flex-column align-items-start">
            <Link
              to={`${value?.reference}?tab=${councilTab[ppCouncil?.councilType?.code]}`}
              className="hover:underline my-1"
              state={{ template: value?.template }}
            >
              - {ppCouncil?.councilType?.name}{' '}
              {value?.template === TEMPLATE[1595] &&
              ppCouncil?.councilType?.code === 'specializationCouncil'
                ? t('council.viewList.andFinance')
                : ''}
            </Link>
            <div className="flex flex-wrap align-items-center">
              <Tag
                style={{
                  maxWidth: '8rem',
                  minWidth: '4rem',
                  textAlign: 'center',
                }}
                severity={
                  councilStatusSeverityMap[ppCouncil?.councilStatus ?? COUNCIL_STATUS.NOT_REVIEWED]
                }
                value={t(
                  `council.viewList.${ppCouncil?.councilStatus ?? COUNCIL_STATUS.NOT_REVIEWED}`
                )}
                rounded
              />

              {hasRequest && (
                <>
                  <i
                    className={`request-${ppCouncil?.id} pi pi-file p-overlay-badge ml-1 text-xl`}
                    data-pr-tooltip={
                      requestStatusIcon?.tooltip ??
                      t(
                        'projectProposal.requestExplanation.statuses.specialistNotRequestExplanationYet'
                      )
                    }
                  >
                    <Badge
                      value={<i className={`${requestStatusIcon?.icon} font-bold`} />}
                      className={requestStatusIcon?.className}
                      style={{ backgroundColor: 'transparent' }}
                    />
                  </i>
                  <Tooltip target={`.request-${ppCouncil?.id}`} />
                </>
              )}
            </div>
          </div>
        );
      });
    }
    return null;
  };
  const councilTypeFilter = () => (
    <MultiSelect
      value={filters.councilTypeId}
      onChange={(e) => {
        const newFilters = { ...filters, councilTypeId: e.value };
        setFilters(newFilters);
      }}
      options={councilTypes}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.councilType')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatStatus = (value) => {
    const statusColorMapping = statusColors.reviseViewList;

    // {
    //   new: 'info',
    //   submit: 'success',
    //   prequalification: 'secondary',
    //   specialization: 'primary',
    //   finance: 'contrast',
    //   approved: 'success',
    //   notQualified: 'danger',
    // };

    const newStatus = getNewStatus(value);

    return (
      <Tag
        style={{ maxWidth: '8rem', backgroundColor: statusColorMapping[newStatus ?? 'new'] }}
        // severity={statusColorMapping[newStatus ?? 'new']}
        value={t(`newStatuses.${newStatus ?? 'new'}`)}
        rounded
      />
    );
  };
  const statusFilter = () => (
    <MultiSelect
      value={filters.statusName}
      options={statuses}
      onChange={(e) => {
        const newFilters = { ...filters, statusName: e.value };
        setFilters(newFilters);
      }}
      optionLabel="name"
      optionValue="code"
      showClear
      filter
      placeholder={t('projectProposal.viewList.status')}
      style={{ minWidth: '12rem' }}
      maxSelectedLabels={3}
    />
  );

  const formatActions = (value) => {
    const menuRef = useRef(null);

    const organizationPrequalificationDeadline = getEndingTimeOfDate(
      getMaxDate(
        value?.program?.organizationPrequalificationDeadline,
        value?.organizationPrequalificationDeadline
      )
    );

    const items = [];

    if (value?.deletedAt === null) {
      // Giải trình chỉnh sửa trước sơ tuyển (giải trình ở đơn vị)
      if (
        value?.status?.name === PROJECT_PROPOSAL_NEW_STATUSES.SUBMIT &&
        value?.program &&
        value?.version === 1 &&
        !value?.projectProposalCouncils?.length &&
        !checkOverdueDatetime(organizationPrequalificationDeadline) &&
        ability.can('request_update', 'project_proposal')
      ) {
        items.push({
          label:
            value?.isEditable === 1
              ? t('projectProposal.requestUpdate.addUpdateRequest')
              : t('projectProposal.requestUpdate.title'),
          command: () => {
            dialogRequestUpdateRef.current.open({ mode: 'requestUpdate', value });
          },
        });
      }

      // Xem lịch sử trạng thái của đề tài
      if (ability.can('read', 'project_proposal')) {
        items.push({
          label: t('projectProposal.viewList.tooltip.viewHistory'),
          command: () => {
            setProjectLogsId(value?.id ?? '');
            setViewHistoryVisible(true);
          },
        });
      }

      // Xem lịch sử gửi email thông báo
      if (ability.can('read', 'project_proposal')) {
        items.push({
          template: () => (
            <div
              className="p-menuitem-link"
              onClick={(e) => {
                setProjectProposalId(value?.id ?? '');
                setViewEmailLogsVisible(true);
                menuRef.current.hide(e);
              }}
              style={{ cursor: 'pointer' }}
            >
              <span className="p-menuitem-text">
                {t('projectProposal.viewList.tooltip.viewEmailLogs')}
              </span>
              {value?.emailLogs?.length > 0 && (
                <Badge
                  value={value?.emailLogs?.length}
                  severity="danger"
                  style={{ marginLeft: '8px' }}
                />
              )}
            </div>
          ),
        });
      }

      // Chỉnh hạn sơ tuyển (đơn vị) cho đề tài
      if (
        value?.status?.name === PROJECT_PROPOSAL_NEW_STATUSES.SUBMIT &&
        value?.program &&
        value?.version === 1 &&
        !value?.projectProposalCouncils?.length &&
        ability.can('update_deadline', 'project_proposal')
      ) {
        items.push({
          label: t('projectProposal.changeDeadline.title'),
          command: () => {
            dialogChangeDeadlineRef.current.open({ value });
          },
        });
      }

      // Giao đề tài và tạo hợp đồng
      if (
        (value?.status?.name === PROJECT_PROPOSAL_NEW_STATUSES.SUBMIT ||
          value?.status?.name === PROJECT_PROPOSAL_NEW_STATUSES.APPROVED) &&
        checkIsAllowedCRUDFromContract(value?.projectType, roles) &&
        ability.can('create', 'project')
      ) {
        items.push({
          template: () => (
            <Link
              to={{
                pathname: `/projects-submit/create/${value?.id}`,
              }}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('projectProposal.viewList.createContract')}
              </span>
            </Link>
          ),
        });
      }

      // Thay đổi chương trình
      if (
        value?.status?.name !== PROJECT_PROPOSAL_NEW_STATUSES.NEW &&
        ability.can('update', 'project_proposal')
      ) {
        items.push({
          label: t('projectProposal.viewList.changeProgram'),
          command: () => {
            setIsVisibleProgramDialog(true);
            setSelectedProgram({
              ...value.program,
              vietnameseName: value.vietnameseName,
              englishName: value.englishName,
              projectProposalId: value.reference,
            });
          },
        });
      }

      // Xóa đề tài
      if (ability.can('delete', 'project_proposal')) {
        items.push({
          label: t('projectProposal.viewList.delete'),
          command: () => {
            dialogConfirmRef.current.open({ mode: 'delete', mutate: mutateDelete, value });
          },
        });
      }
    } else {
      // Khôi phục đề tài
      if (ability.can('restore', 'project_proposal')) {
        items.push({
          label: t('projectProposal.viewList.restore'),
          command: () => {
            dialogConfirmRef.current.open({ mode: 'restore', mutate: mutateRestore, value });
          },
        });
      }
    }

    const hasNotifications =
      value?.deletedAt === null &&
      value?.status?.name === PROJECT_PROPOSAL_NEW_STATUSES.SUBMIT &&
      value?.program &&
      value?.version === 1 &&
      !value?.projectProposalCouncils?.length &&
      !checkOverdueDatetime(organizationPrequalificationDeadline) &&
      value?.isEditable === 1;

    return (
      <div className="flex justify-content-center">
        <Menu model={items} popup ref={menuRef} style={{ minWidth: '250px' }} />
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Button
            icon="pi pi-ellipsis-v"
            className="p-button-rounded p-button-text"
            onClick={(event) => menuRef.current.toggle(event)}
          />
          {hasNotifications && (
            <Badge
              severity="danger"
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                minWidth: '0.5rem',
                height: '0.5rem',
                fontSize: '0.5rem',
                padding: '0',
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        handleAllRowUnselect();
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        handleAllRowUnselect();
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      disabled={
        !totalExpenditureSchema.isValidSync({
          min: filters?.totalExpenditure?.min ?? undefined,
          max: filters?.totalExpenditure?.max ?? undefined,
        })
      }
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const renderHeader = () => (
    <div className="flex justify-content-between">
      <Button
        type="button"
        icon="pi pi-filter"
        label={t('projectProposal.viewList.searchFilters')}
        outlined
        onClick={() => setIsVisibleSearchFilters(true)}
      />
    </div>
  );

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, error)}</h4>;
  }

  return (
    <div className="card relative">
      <div className="flex align-items-center justify-content-between gap-2 pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('projectProposal.viewList.title')}
        </h4>
        <div className="flex gap-2">
          <Can do="export_excel" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('projectProposal.viewList.tooltip.excel')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isExportExcelLoading },
                  { 'pi-file-excel': !isExportExcelLoading }
                )}
                severity="success"
                onClick={handleExportExcel}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isExportExcelLoading
                }
              />
            </div>
          </Can>
          {/* request open revise */}
          <span
            className="inline-block"
            data-pr-tooltip={
              selectedItems.some((item) => item.projectType !== 'C')
                ? t('projectProposal.viewList.tooltip.requestEditUnitTooltip')
                : ''
            }
            data-pr-position="right top"
          >
            <Button
              type="button"
              label={t('projectProposal.viewList.tooltip.requestEditUnit')}
              icon={classNames('pi pi-file-edit')}
              severity="warning"
              onClick={() => {
                dialogOpenRevisesRef.current.open();
              }}
              disabled={
                (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                selectedItems.some((item) => item.projectType !== 'C')
              }
            />
            <Tooltip target=".inline-block" />
          </span>
          <Can do="read" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('projectProposal.viewList.tooltip.download')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isDownloadZipLoading },
                  { 'pi pi-download': !isDownloadZipLoading }
                )}
                severity="info"
                onClick={handleDownloadZip}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isDownloadZipLoading
                }
              />
            </div>
          </Can>
          <Can do="read" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('projectProposal.viewList.tooltip.sendRemindEmail')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isSendRemindEmailLoading },
                  { 'pi-envelope': !isSendRemindEmailLoading }
                )}
                onClick={() => {
                  dialogSendMailRef.current.open();
                }}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isSendRemindEmailLoading
                }
              />
            </div>
          </Can>
        </div>
      </div>

      {/* Custom checkbox selectAll */}
      {!isLoading && data?.data?.length ? (
        <Checkbox
          style={{ position: 'absolute', top: '192px', left: '43px', zIndex: 10 }}
          checked={isSelectedAll}
          onChange={() => (!isSelectedAll ? handleAllRowSelect() : handleAllRowUnselect())}
        />
      ) : null}

      <DataTable
        value={data?.data}
        className="project-list"
        dataKey="id"
        header={renderHeader}
        showGridlines
        scrollable
        selectionMode="multiple"
        selection={selectedItems}
        showSelectAll={false}
        onRowSelect={handleRowSelect}
        onRowUnselect={handleRowUnselect}
        isDataSelectable={isRowSelectable}
        rowClassName={rowClassName}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
      >
        {ability.can('export_excel', 'project_proposal') ? (
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} frozen />
        ) : null}
        <Column
          header={t('projectProposal.viewList.name')}
          headerStyle={filterActiveStyle('vietnameseName')}
          body={formatName}
          filter
          filterField="_vietnameseName"
          filterElement={vietnameseNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, vietnameseName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
          frozen
          alignFrozen="left"
        />
        <Column
          field="projectType"
          headerStyle={{ ...filterActiveStyle('projectType'), justifyContent: 'center' }}
          header={t('projectProposal.viewList.projectType')}
          body={formatProjectType}
          filter
          filterField="_projectType"
          filterElement={projectTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, projectType: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '6rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.viewList.owner')}
          headerStyle={{ ...filterActiveStyle('ownerName'), justifyContent: 'center' }}
          body={formatOwner}
          filter
          filterField="_ownerName"
          filterElement={ownerNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, ownerName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />

        <Column
          header={t('projectProposal.viewList.organization')}
          headerStyle={{ ...filterActiveStyle('organizationId'), justifyContent: 'center' }}
          body={formatOrganization}
          filter
          filterField="_organization"
          filterElement={organizationFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu={organizations?.length > 1}
          onFilterClear={() => {
            const newFilters = { ...filters, organizationId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.viewList.council')}
          headerStyle={{
            ...filterActiveStyle('councilTypeId'),
            textAlign: 'center',
          }}
          body={formatCouncil}
          filter
          filterField="_councilType"
          filterElement={councilTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              councilTypeId: [],
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
        />
        <Column
          field="status.name"
          header={t('projectProposal.viewList.status')}
          headerStyle={filterActiveStyle('statusName')}
          body={formatStatus}
          filter
          filterField="_status"
          filterElement={statusFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, statusName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.viewList.researchField')}
          headerStyle={{ ...filterActiveStyle('researchFieldId'), justifyContent: 'center' }}
          body={formatResearchFields}
          filter
          filterField="_researchField"
          filterElement={researchFieldFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, researchFieldId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', textAlign: 'center' }}
        />
        <Column
          field="researchType"
          header={t('projectProposal.viewList.researchType')}
          headerStyle={{ ...filterActiveStyle('researchTypeId'), justifyContent: 'center' }}
          body={formatResearchType}
          filter
          filterField="_researchType"
          filterElement={researchTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, researchTypeId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        {/* <Column
          field="implementationMonths"
          header={t('projectProposal.viewList.implementationTime')}
          dataType="numeric"
          style={{ minWidth: '6rem', textAlign: 'center' }}
        /> */}
        <Column
          field="totalExpenditure"
          header={
            <span style={{ whiteSpace: 'pre-line' }}>
              {t('projectProposal.viewList.totalExpenditure')}
            </span>
          }
          headerStyle={{ ...filterActiveStyle('totalExpenditure'), justifyContent: 'center' }}
          body={formatTotalExpenditure}
          filter
          filterField="_totalExpenditure"
          filterElement={totalExpenditureFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, totalExpenditure: {} };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            const isValid = totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            });
            if (isValid) {
              setAppliedFilters(filters);
            }
          }}
          dataType="numeric"
          style={{ minWidth: '9rem', textAlign: 'center' }}
        />
        <Column
          field="program.name"
          header={t('projectProposal.viewList.program')}
          headerStyle={filterActiveStyle('programId')}
          dataType="date"
          filter
          filterField="_program"
          filterElement={programFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, programId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem' }}
        />
        <Column
          header={t('projectProposal.general.researchTime.yearProposed')}
          headerStyle={{ ...filterActiveStyle('yearProposed'), justifyContent: 'center' }}
          field="yearProposed"
          filter
          filterField="_yearProposed"
          filterElement={yearProposedFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, yearProposed: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.viewList.submittedAt')}
          headerStyle={filterActiveStyle('submittedAt')}
          body={formatSubmittedAt}
          dataType="date"
          filter
          filterField="_submittedAt"
          filterElement={submittedAtFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              submittedAt: {},
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem' }}
        />
        <Column
          header={t('projectProposal.action')}
          body={formatActions}
          style={{ minWidth: '6rem', textAlign: 'center' }}
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        showMode={showMode}
        setShowMode={setShowMode}
        pageName="projects-revise"
      />

      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          projectTypes={projectTypes}
          organizations={organizations}
          researchFields={researchFields}
          researchTypes={researchTypes}
          programs={programs}
          councilTypes={councilTypes}
          statuses={statuses}
        />
      )}

      <Menu model={approvalItems} popup ref={approvalMenu} />

      <ViewHistory
        data={projectHistory}
        visible={viewHistoryVisible}
        setVisible={setViewHistoryVisible}
      />

      <ViewEmailLogs
        projectProposalId={projectProposalId}
        visible={viewEmailLogsVisible}
        setVisible={setViewEmailLogsVisible}
      />

      <SendMail
        ref={dialogSendMailRef}
        handleSendMail={handleSendMail}
        isLoading={isSendRemindEmailLoading}
      />
      <Confirm ref={dialogConfirmRef} refetch={refetch} />

      <RequestUpdateForm ref={dialogRequestUpdateRef} />
      <OpenRevisesForm
        ref={dialogOpenRevisesRef}
        selectedItems={selectedItems.map((e) => e.id)}
        unselectedItems={unselectedItems.map((e) => e.id)}
        isSelectedAll={isSelectedAll}
        isUnselectedSome={isUnselectedSome}
        filters={filters}
      />

      <ChangeDeadline
        ref={dialogChangeDeadlineRef}
        mutate={mutateChangeDeadline}
        isLoading={isChangeDeadlineLoading}
        refetchProjectProposals={refetch}
      />

      <CreateContractForm ref={dialogCreateContractRef} />

      <ProgramDialog
        isVisible={isVisibleProgramDialog}
        setIsVisible={setIsVisibleProgramDialog}
        currentData={selectedProgram}
        onSelectedChange={handleChangeProgram}
        refetch={refetch}
      />

      <ErrorDialog
        title={t('projectProposal.exportExcelErrorTitle')}
        isError={isExportExcelError}
        error={exportExcelError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('projectProposal.downloadZipErrorTitle')}
        isError={isDownloadZipError}
        error={downloadZipError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('projectProposal.sendRemindEmailErrorTitle')}
        isError={isSendRemindEmailError}
        error={sendRemindEmailError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
    </div>
  );
  // #endregion Render
}

export default ReviseProjectProposalViewList;
